// SVG placeholder fallback premium (dégradé bordeaux/gris avec icône musicale discrète)
const FALLBACK_IMAGE = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400"><defs><linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#2a0a1a"/><stop offset="100%" stop-color="#1a1a2e"/></linearGradient></defs><rect width="400" height="400" fill="url(#bg)"/><g transform="translate(200,200)" fill="none" stroke="rgba(255,255,255,0.15)" stroke-width="2"><circle cx="0" cy="0" r="120"/><circle cx="0" cy="0" r="80"/><circle cx="0" cy="0" r="40"/></g><path d="M175 130 L175 270 L250 240 L250 160 Z" fill="rgba(255,255,255,0.12)"/></svg>');

const IMAGEKIT_BASE_URL = 'https://ik.imagekit.io/a6ywpqgqor';
const IMAGEKIT_FALLBACK_BASE = 'https://ik.imagekit.io/a6ywpqgqor';

/**
 * Vérifie si une URL provient d'ImageKit.
 */
function isImageKitUrl(url) {
  if (!url) return false;
  return url.includes('ik.imagekit.io') || url.startsWith(IMAGEKIT_BASE_URL);
}

/**
 * Vérifie si une URL provient de Cloudinary.
 */
function isCloudinaryUrl(url) {
  if (!url) return false;
  return url.includes('res.cloudinary.com');
}

/**
 * Convertit une URL Cloudinary vers son équivalent ImageKit pour le fallback.
 * Extrait le nom de fichier et le dossier depuis l'URL Cloudinary
 * et reconstruit une URL ImageKit pointant vers le même fichier.
 */
export function cloudinaryToImageKitUrl(cloudinaryUrl) {
  if (!cloudinaryUrl || !isCloudinaryUrl(cloudinaryUrl)) return null;

  try {
    const knownFolders = ['Covers', 'ProfilePic', 'merch'];
    const pathParts = cloudinaryUrl.split('/');
    const filename = pathParts[pathParts.length - 1].split('?')[0];
    if (!filename) return null;

    for (const folder of knownFolders) {
      if (cloudinaryUrl.includes(`/${folder}/`)) {
        return `${IMAGEKIT_FALLBACK_BASE}/${folder}/${filename}`;
      }
    }

    const versionMatch = cloudinaryUrl.match(/\/v\d+\/([^/]+)\//);
    if (versionMatch) {
      return `${IMAGEKIT_FALLBACK_BASE}/${versionMatch[1]}/${filename}`;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Retourne une URL d'image optimisée.
 * 
 * - Si l'URL est déjà ImageKit → ajoute les paramètres de transformation (tr=w-XXX,q-80)
 * - Si l'URL est Cloudinary → servie TELLE QUELLE (le fallback onError gère la conversion)
 * - Si l'URL est vide → retourne le fallback SVG
 *
 * @param {string} url - URL originale de l'image
 * @param {Object} [options]
 * @param {number} [options.width] - Largeur souhaitée
 * @param {number} [options.height] - Hauteur souhaitée
 * @param {number} [options.quality=80] - Qualité (1-100)
 * @returns {string} URL optimisée
 */
export function getOptimizedImageUrl(url, options = {}) {
  if (!url) return FALLBACK_IMAGE;

  const { width, height, quality = 80 } = options;

  if (isImageKitUrl(url)) {
    const params = [];
    if (width) params.push(`w-${width}`);
    if (height) params.push(`h-${height}`);
    params.push(`q-${quality}`);
    params.push('f-auto');
    params.push('pg-progressive');

    const trString = params.join(',');
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}tr=${trString}`;
  }

  // URLs Cloudinary : servies telles quelles
  // Le fallback via onError → handleImageError gère la conversion Cloudinary→ImageKit
  return url;
}

/**
 * Gestionnaire d'erreur multi-niveaux pour les images.
 * 
 * 🔵 Niveau 1 : Si l'URL est Cloudinary (échec quota 401) → tente ImageKit
 * 🔵 Niveau 2 : Si ImageKit échoue aussi → SVG placeholder FALLBACK_IMAGE
 *
 * Usage : <img src={url} onError={handleImageError} />
 */
export function handleImageError(e) {
  const target = e.target;
  if (!target || !target.src) return;

  // Déjà le fallback final ? On arrête pour éviter la boucle infinie
  if (target.src === FALLBACK_IMAGE) return;

  // 🔵 Niveau 1 : Cloudinary → tenter ImageKit en fallback
  if (isCloudinaryUrl(target.src)) {
    const imageKitUrl = cloudinaryToImageKitUrl(target.src);
    if (imageKitUrl) {
      target.removeAttribute('srcset');
      target.removeAttribute('sizes');
      target.src = imageKitUrl;
      return;
    }
  }

  // 🔵 Niveau 2 (final) : remplacer par le placeholder SVG
  target.removeAttribute('srcset');
  target.removeAttribute('sizes');
  target.src = FALLBACK_IMAGE;
}
